import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useUnifiedUser } from './useUnifiedUser';

interface TeamInfo {
  team_id: number;
  team_name: string;
}

export const useTeamAccessCode = () => {
  const [loading, setLoading] = useState(false);
  const { user: unifiedUser } = useUnifiedUser();

  const validateCode = async (code: string, userId: string): Promise<TeamInfo | null> => {
    setLoading(true);
    try {
      if (!unifiedUser) {
        toast.error('You must be logged in to join a team');
        return null;
      }

      // Use the enhanced function that also adds to team chat
      const { data, error } = await supabase
        .rpc('validate_team_access_code_with_chat', {
          p_access_code: code.toUpperCase(),
          p_user_id: userId,
          p_user_type: unifiedUser.userType
        });

      if (error) throw error;

      const result = data as { success: boolean; error?: string; team_id?: number; team_name?: string; message?: string };

      if (result.success) {
        toast.success(result.message || `Successfully joined ${result.team_name}!`);
        return {
          team_id: result.team_id!,
          team_name: result.team_name!
        };
      } else {
        toast.error(result.error || 'Invalid team code');
        return null;
      }
    } catch (error: any) {
      console.error('Error validating team code:', error);
      toast.error('Failed to validate team code: ' + error.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const generateCode = async (teamId: number): Promise<string | null> => {
    setLoading(true);
    try {
      if (!unifiedUser) {
        toast.error('You must be logged in to generate team codes');
        return null;
      }

      const { data, error } = await supabase
        .rpc('generate_team_access_code', {
          p_team_id: teamId,
          p_user_id: unifiedUser.id,
          p_user_type: unifiedUser.userType
        });

      if (error) throw error;

      console.log('Raw function response:', data);
      
      // The function now returns a JSON object with success/error
      const result = data as { success: boolean; error?: string; access_code?: string };
      
      if (result.success && result.access_code) {
        toast.success('Team access code generated successfully!');
        return result.access_code;
      } else {
        toast.error(result.error || 'Failed to generate access code');
        return null;
      }
    } catch (error: any) {
      console.error('Error generating team code:', error);
      toast.error('Failed to generate team code: ' + error.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    validateCode,
    generateCode,
    loading
  };
};